/**
 * SavedCardsList
 * ----------------------------------------------------------------------------
 * Lista os cartões salvos (tokenizados) de um cliente CRM com bandeira,
 * últimos 4 dígitos, validade, status (ativo/com falhas) e vínculo com
 * assinaturas. Permite revogar (soft-delete) com validação de "default em
 * assinatura ativa" feita no backend.
 *
 * Usado em CustomerFinancialTab (sub-aba "Cartões salvos") e — em modo
 * compacto (selectable) — em modais de assinatura e criação de assinatura.
 *
 * RELEASE: 2026-06-20 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import { CreditCard, Trash2, Loader2, ShieldAlert, CheckCircle2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSavedCards, useRevokeSavedCard, type SavedCard } from '@/hooks/financial/useSavedCards';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SavedCardsListProps {
  customerId: string;
  /** Permite revogar cartões. Default: true. */
  canRevoke?: boolean;
  /** Quando definido, mostra cartão como selecionável com radio. */
  selectable?: boolean;
  selectedCardId?: string | null;
  onSelect?: (cardId: string | null) => void;
}

export function SavedCardsList({
  customerId,
  canRevoke = true,
  selectable = false,
  selectedCardId,
  onSelect,
}: SavedCardsListProps) {
  const { data: cards, isLoading } = useSavedCards(customerId);
  const revoke = useRevokeSavedCard();
  const [cardToRevoke, setCardToRevoke] = useState<SavedCard | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!cards || cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center text-muted-foreground">
        <CreditCard className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm font-medium">Nenhum cartão salvo</p>
        <p className="text-xs mt-1 max-w-sm">
          O cliente pode salvar um cartão no próximo pagamento pelo link público de checkout.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {selectable && (
          <label className="flex items-center gap-3 p-3 rounded-md border cursor-pointer hover:bg-muted/40">
            <input
              type="radio"
              name="saved-card-select"
              checked={!selectedCardId}
              onChange={() => onSelect?.(null)}
              className="h-4 w-4"
            />
            <div className="flex-1">
              <p className="text-sm font-medium">Sem cartão padrão</p>
              <p className="text-[11px] text-muted-foreground">
                Enviar link de checkout em cada ciclo
              </p>
            </div>
          </label>
        )}

        {cards.map(card => {
          const isFailed = card.failure_count > 0 && !card.last_success_at;
          const isDefaultOfActive = card.default_of_subscriptions.some(
            s => s.status === 'ativa',
          );
          const Wrapper: any = selectable ? 'label' : 'div';
          return (
            <Wrapper
              key={card.id}
              className={`flex items-center gap-3 p-3 rounded-md border ${
                selectable ? 'cursor-pointer hover:bg-muted/40' : ''
              } ${selectedCardId === card.id ? 'ring-2 ring-primary border-primary' : ''}`}
            >
              {selectable && (
                <input
                  type="radio"
                  name="saved-card-select"
                  checked={selectedCardId === card.id}
                  onChange={() => onSelect?.(card.id)}
                  className="h-4 w-4"
                />
              )}

              <div className="h-9 w-12 rounded bg-muted flex items-center justify-center shrink-0">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">
                    {card.brand?.toUpperCase() || 'CARTÃO'} •••• {card.last4}
                  </span>
                  {isFailed && (
                    <Badge variant="destructive" className="text-[10px] gap-1">
                      <ShieldAlert className="h-3 w-3" />
                      Com falhas
                    </Badge>
                  )}
                  {!isFailed && card.last_success_at && (
                    <Badge variant="outline" className="text-[10px] gap-1 text-green-600 border-green-200">
                      <CheckCircle2 className="h-3 w-3" />
                      Ativo
                    </Badge>
                  )}
                  {isDefaultOfActive && (
                    <Badge variant="secondary" className="text-[10px]">
                      Padrão em assinatura
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                  </span>
                  {card.holder_name_masked && <span>{card.holder_name_masked}</span>}
                  <span>
                    Salvo em{' '}
                    {format(new Date(card.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                </div>
              </div>

              {canRevoke && !selectable && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setCardToRevoke(card)}
                  disabled={revoke.isPending}
                  title="Revogar cartão"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </Wrapper>
          );
        })}
      </div>

      <AlertDialog open={!!cardToRevoke} onOpenChange={o => !o && setCardToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar cartão?</AlertDialogTitle>
            <AlertDialogDescription>
              O cartão{' '}
              <strong>
                {cardToRevoke?.brand?.toUpperCase()} •••• {cardToRevoke?.last4}
              </strong>{' '}
              não poderá mais ser usado para cobranças. Esta ação não pode ser desfeita.
              {cardToRevoke?.default_of_subscriptions.some(s => s.status === 'ativa') && (
                <span className="block mt-2 text-destructive">
                  Atenção: este cartão é padrão em assinatura(s) ativa(s). A revogação será
                  bloqueada pelo servidor — troque o cartão padrão antes.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!cardToRevoke) return;
                try {
                  await revoke.mutateAsync({ cardId: cardToRevoke.id, customerId });
                  setCardToRevoke(null);
                } catch {
                  /* toast já tratado no hook */
                }
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Revogar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
